import React, { useRef, useLayoutEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { WORLD, ZONES, BANK } from '../config/zones';
import { mulberry32 } from './rng';
import { shared } from './shared';
import { useStore } from '../store';

const dummy = new THREE.Object3D();

function useMatrices(ref, transforms) {
  useLayoutEffect(() => {
    if (!ref.current) return;
    transforms.forEach((t, i) => {
      dummy.position.set(t.x, t.y, t.z);
      dummy.rotation.set(0, t.ry, 0);
      dummy.scale.set(t.s, t.sy, t.s);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
    ref.current.computeBoundingSphere();
  }, [ref, transforms]);
}

// Generate scattered tree transforms for one bank, avoiding the river corridor
// and carving open clearings around each zone's structures + the spawn point.
function useBankTrees(sign, seed, count) {
  return useMemo(() => {
    const rand = mulberry32(seed);
    const arr = [];
    const inner = WORLD.bankInner + 6;
    const outer = WORLD.bankOuter;
    const clearingZ = sign * 60;
    let attempts = 0;
    while (arr.length < count && attempts < count * 6) {
      attempts++;
      const x = WORLD.xMin + rand() * (WORLD.xMax - WORLD.xMin);
      const z = sign * (inner + rand() * (outer - inner));
      // Skip zone clearings (space for hives/forts + animals)
      let blocked = false;
      for (const zone of ZONES) {
        const dx = x - zone.x;
        const dz = z - clearingZ;
        if (dx * dx + dz * dz < 26 * 26) { blocked = true; break; }
      }
      // Skip the spawn corridor near the river at x~0
      if (Math.abs(x) < 22 && Math.abs(z) < 42) blocked = true;
      if (blocked) continue;
      const s = 2.2 + rand() * 3.0;
      arr.push({ x, y: 0, z, s, sy: 1 + rand() * 0.6, ry: rand() * Math.PI * 2 });
    }
    return arr;
  }, [sign, seed, count]);
}

/**
 * Progressive (Left) bank: dense, fluid, glowing canopy — organic icosahedron foliage.
 * Traditional (Right) bank: rigid, architectural canopy — coniferous/boxy foliage.
 * Both rendered with InstancedMesh for high performance; scene fog handles distance LOD.
 */
export default function Trees() {
  const leftTrunk = useRef();
  const leftCanopy = useRef();
  const rightTrunk = useRef();
  const rightCanopy = useRef();

  const COUNT = 300;
  const ws = useStore.getState().worldSeed;
  const left = useBankTrees(-1, 1337 + ws, COUNT);
  const right = useBankTrees(1, 4242 + ws, COUNT);

  // Trunk transforms are the tree transforms scaled thin; canopy sits on top.
  const leftTrunkT = useMemo(() => left.map((t) => ({ ...t, y: t.s * 1.6, s: t.s * 0.16, sy: t.s * 1.6 })), [left]);
  const leftCanopyT = useMemo(() => left.map((t) => ({ ...t, y: t.s * 3.4, s: t.s * 1.35, sy: t.s * 1.5 })), [left]);
  const rightTrunkT = useMemo(() => right.map((t) => ({ ...t, y: t.s * 1.6, s: t.s * 0.2, sy: t.s * 1.6 })), [right]);
  const rightCanopyT = useMemo(() => right.map((t) => ({ ...t, y: t.s * 3.6, s: t.s * 1.15, sy: t.s * 1.8 })), [right]);

  useMatrices(leftTrunk, leftTrunkT);
  useMatrices(leftCanopy, leftCanopyT);
  useMatrices(rightTrunk, rightTrunkT);
  useMatrices(rightCanopy, rightCanopyT);

  const cols = useMemo(() => ({
    lDay: new THREE.Color('#0f4d3a'), lNight: new THREE.Color('#12377a'),
    rDay: new THREE.Color('#000000'), rNight: new THREE.Color('#9a1414'),
  }), []);

  // Night bioluminescence (blue left / red right) + storm-driven canopy sway.
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const storm = shared.env.storm;
    const night = shared.env.night;
    const sway = Math.sin(t * 3) * storm * 2.4;
    if (leftCanopy.current) {
      leftCanopy.current.position.x = sway;
      const m = leftCanopy.current.material;
      m.emissiveIntensity = 0.5 + night * 1.2;
      m.emissive.copy(cols.lDay).lerp(cols.lNight, night);
    }
    if (rightCanopy.current) {
      rightCanopy.current.position.x = sway * 0.8;
      const m = rightCanopy.current.material;
      m.emissiveIntensity = night * 1.5;
      m.emissive.copy(cols.rDay).lerp(cols.rNight, night);
    }
  });

  return (
    <group>
      {/* LEFT BANK — Progressive: glowing organic canopy */}
      <instancedMesh ref={leftTrunk} args={[undefined, undefined, COUNT]} castShadow frustumCulled>
        <cylinderGeometry args={[1, 1.3, 2, 5]} />
        <meshStandardMaterial color="#3a2a1e" roughness={1} />
      </instancedMesh>
      <instancedMesh ref={leftCanopy} args={[undefined, undefined, COUNT]} castShadow frustumCulled>
        <icosahedronGeometry args={[1.6, 1]} />
        <meshStandardMaterial color="#1f8f5f" emissive="#0f4d3a" emissiveIntensity={0.55} roughness={0.7} flatShading />
      </instancedMesh>

      {/* RIGHT BANK — Traditional: rigid architectural canopy */}
      <instancedMesh ref={rightTrunk} args={[undefined, undefined, COUNT]} castShadow frustumCulled>
        <cylinderGeometry args={[1, 1.1, 2, 6]} />
        <meshStandardMaterial color="#4a3626" roughness={1} />
      </instancedMesh>
      <instancedMesh ref={rightCanopy} args={[undefined, undefined, COUNT]} castShadow frustumCulled>
        <coneGeometry args={[1.5, 3, 6]} />
        <meshStandardMaterial color="#2f4a24" emissive="#000000" emissiveIntensity={0} roughness={0.95} flatShading />
      </instancedMesh>
    </group>
  );
}
